import React, {useMemo} from 'react';
import classNames from 'classnames';
import {IntlShape} from 'react-intl';
import {SVGS} from '../../../../assets/images';
import TimeReportMobileCard from '../time-report-mobile-card';
import TimeReportEmployeeTotalBar from '../time-report-employee-total-bar';
import {
    GetGroupedAbsenceHours,
    GetGroupedApprovedHours,
    GetGroupedBilledHours,
    GetGroupedOvertimes,
    GetGroupedTimeBankBalance,
    GetGroupedWorkHours,
    TimeReportItem
} from '../../../../features/time-report/types';
import { TimeSheetGroupType } from '../../../../features/schedule/types';
import {TimeReportV2DailyEmployeeTotal} from '../../../../features/time-report-v2/types';
import styles from './time-report-mobile-view.module.scss';
import {getTimeReportEmployeePlannedHours} from '../../helpers/time-report-group-hours';

type Props = {
    data?: TimeReportItem[];
    intl: IntlShape;
    className?: string;
    groupBy: TimeSheetGroupType;
    detailedByDay?: boolean;
    employeeTotals?: TimeReportV2DailyEmployeeTotal[];
};

type EmployeeGroup = {
    key: string;
    title: string;
    items: TimeReportItem[];
};

type EmployeeSectionProps = {
    group: EmployeeGroup;
    intl: IntlShape;
    defaultExpanded?: boolean;
    employeeTotalsById: Map<number, number>;
};

const EmployeeSection: React.FC<EmployeeSectionProps & { detailedByDay?: boolean; groupBy: TimeSheetGroupType }> = ({
    group,
    intl,
    defaultExpanded = false,
    detailedByDay = false,
    groupBy,
    employeeTotalsById
}) => {
    const [expanded, setExpanded] = React.useState<boolean>(defaultExpanded);

    const totals = useMemo(() => ({
        workHours: GetGroupedWorkHours(group.items),
        overtimeHours: GetGroupedOvertimes(group.items),
        absenceHours: GetGroupedAbsenceHours(group.items),
        approvedHours: GetGroupedApprovedHours(group.items),
        billableHours: GetGroupedBilledHours(group.items),
        timeBankBalance: GetGroupedTimeBankBalance(group.items),
        plannedHours: groupBy === TimeSheetGroupType.EMPLOYEEVIEW
            ? getTimeReportEmployeePlannedHours(group.items, employeeTotalsById, detailedByDay)
            : undefined
    }), [detailedByDay, employeeTotalsById, group.items, groupBy]);

    const isProjectView = groupBy === TimeSheetGroupType.PROJECTVIEW;

    const toggle = () => setExpanded((state) => !state);
    const handleButtonClick = (event?: React.MouseEvent<HTMLButtonElement>) => {
        event?.stopPropagation();
        toggle();
    };

    return (
        <div className={styles.section}>
            <div
                className={classNames(styles.header, { [styles.headerCollapsed]: !expanded })}
                role='button'
                tabIndex={0}
                onClick={toggle}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggle();
                    }
                }}
            >
                <button
                    type='button'
                    className={styles.headerToggleButton}
                    onClick={handleButtonClick}
                    aria-label={expanded ? 'Collapse' : 'Expand'}
                >
                    <SVGS.RightChevronIcon
                        className={classNames(styles.headerToggleIcon, {
                            [styles.headerToggleIconExpanded]: expanded
                        })}
                    />
                </button>
                <span className={styles.employeeName}>{group.title}</span>
            </div>

            {expanded && (
                <div className={styles.content}>
                    <TimeReportEmployeeTotalBar
                        intl={intl}
                        workedHours={totals.workHours}
                        overtimeHours={totals.overtimeHours}
                        absenceHours={totals.absenceHours}
                        approvedHours={totals.approvedHours}
                        billableHours={totals.billableHours}
                        timeBankBalance={totals.timeBankBalance}
                        plannedHours={totals.plannedHours}
                        className={styles.totalBar}
                    />

                    <div className={styles.cards}>
                        {group.items.map((item) => (
                            <TimeReportMobileCard
                                key={`${group.key}-${item.projectId}-${item.timeshiftTypeId}-${item.timeshiftType}-${item.id}`}
                                item={item}
                                intl={intl}
                                detailedByDay={detailedByDay}
                                isProjectView={isProjectView}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const TimeReportMobileView: React.FC<Props> = ({
    data = [],
    intl,
    className,
    groupBy,
    detailedByDay = false,
    employeeTotals = []
}) => {
    const employeeTotalsById = useMemo(
        () => new Map(employeeTotals.map((total) => [total.employeeId, total.plannedHours])),
        [employeeTotals]
    );
    const groups: EmployeeGroup[] = useMemo(() => {
        const grouped = data.reduce<Map<string, EmployeeGroup>>((acc, item) => {
            const key = groupBy === TimeSheetGroupType.EMPLOYEEVIEW
                ? `employee-${item.id}`
                : `${item.timeshiftType}-${item.projectId}`;
            const title = groupBy === TimeSheetGroupType.EMPLOYEEVIEW
                ? item.employeeName
                : item.projectName;
            const existing = acc.get(key) ?? { key, title, items: [] };
            existing.items.push(item);
            acc.set(key, existing);
            return acc;
        }, new Map<string, EmployeeGroup>());

        const result = Array.from(grouped.values());
        return result;
    }, [data, groupBy]);

    return (
        <div className={classNames(styles.list, className)}>
            {groups.map((group) => (
                <EmployeeSection
                    key={group.key}
                    group={group}
                    intl={intl}
                    detailedByDay={detailedByDay}
                    groupBy={groupBy}
                    employeeTotalsById={employeeTotalsById} />
            ))}
        </div>
    );
};

export default TimeReportMobileView;
